/**
 * @fileoverview App shell for the internal Hans ops console. Mirrors OrgLayout
 * — fixed 256px left rail on md+, top bar + drawer on mobile — with ops nav
 * (Flags / Activity / Account) and a Sign out action. Reads OpsAuthContext for
 * the signed-in operator's name.
 *
 * Deliberately never co-branded: this is Hans's own internal surface, so it
 * always reads as Hans regardless of any org the operator might also belong to.
 */

import { useContext, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Icon } from "@/components/studio/primitives";
import { OpsAuthContext } from "@/App";

const SIDEBAR_W = 256;

const NAV = [
  { id: "flags", label: "Flags", icon: "settings", path: "/ops" },
  { id: "founding", label: "Founding", icon: "star", path: "/ops/founding" },
  { id: "activity", label: "Activity", icon: "clock", path: "/ops/activity" },
  { id: "account", label: "Account", icon: "user", path: "/ops/account" },
];

function activeNavId(pathname) {
  if (pathname.startsWith("/ops/founding")) return "founding";
  if (pathname.startsWith("/ops/activity")) return "activity";
  if (pathname.startsWith("/ops/account")) return "account";
  return "flags";
}

function NavItem({ item, active, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item.path)}
      aria-current={active ? "page" : undefined}
      data-testid={`ops-nav-${item.id}`}
      className={[
        "flex items-center gap-[14px] rounded-[12px] border px-[14px] py-[11px] text-left transition-colors duration-150",
        active
          ? "border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)]"
          : "border-transparent hover:bg-[var(--studio-raised)]",
      ].join(" ")}
    >
      <Icon
        name={item.icon}
        size={20}
        strokeWidth={active ? 2 : 1.7}
        className={active ? "text-[var(--accent-yellow-text)]" : "text-[var(--text-secondary)]"}
      />
      <span
        className={[
          "flex-1 font-sans text-base",
          active ? "font-semibold text-[var(--text-primary)]" : "font-medium text-[var(--text-secondary)]",
        ].join(" ")}
      >
        {item.label}
      </span>
      {active && <span className="h-1.5 w-1.5 rounded-full bg-[var(--accent-yellow)]" />}
    </button>
  );
}

function SidebarInner({ onNavigate }) {
  const { admin, logout } = useContext(OpsAuthContext) || {};
  const navigate = useNavigate();
  const location = useLocation();
  const active = activeNavId(location.pathname);

  const go = (path) => {
    navigate(path);
    onNavigate?.();
  };

  const who = `${admin?.first_name || ""} ${admin?.last_name || ""}`.trim() || admin?.email || "Operator";

  return (
    <div className="flex h-full flex-col px-4 pb-5 pt-[26px]">
      {/* Brand */}
      <div className="flex items-center gap-3 px-2 pb-[22px]">
        <div className="flex h-[42px] w-[42px] items-center justify-center overflow-hidden rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-base)]">
          <Icon name="shield" size={22} strokeWidth={2} className="text-[var(--accent-yellow-text)]" />
        </div>
        <div className="min-w-0">
          <div className="truncate font-sans text-lg font-bold leading-none text-[var(--text-primary)]">Hans Ops</div>
          <div className="mt-[3px] truncate font-sans text-[13px] text-[var(--text-tertiary)]">{who}</div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex flex-col gap-1">
        {NAV.map((item) => (
          <NavItem key={item.id} item={item} active={active === item.id} onSelect={go} />
        ))}
      </nav>

      <div className="flex-1" />

      {/* Sign out pinned to the bottom */}
      <div className="border-t border-[var(--studio-divider)] pt-3">
        <button
          type="button"
          onClick={() => logout?.()}
          data-testid="ops-signout"
          className="flex w-full items-center gap-[14px] rounded-[12px] border border-transparent px-[14px] py-[11px] text-left transition-colors duration-150 hover:bg-[var(--studio-raised)]"
        >
          <Icon name="logout" size={20} strokeWidth={1.7} className="text-[var(--text-secondary)]" />
          <span className="font-sans text-base font-medium text-[var(--text-secondary)]">Sign out</span>
        </button>
      </div>
    </div>
  );
}

export default function OpsLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setDrawerOpen(false);
    document.getElementById("ops-main")?.scrollTo?.({ top: 0 });
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  return (
    <div className="studio min-h-screen bg-[var(--studio-base)] text-[var(--text-primary)]">
      {/* Desktop rail */}
      <aside
        className="fixed inset-y-0 left-0 z-40 hidden border-r border-[var(--studio-divider)] bg-[var(--studio-sidebar)] md:block"
        style={{ width: SIDEBAR_W }}
      >
        <SidebarInner />
      </aside>

      {/* Mobile top bar */}
      <header className="fixed inset-x-0 top-0 z-40 flex h-14 items-center justify-between border-b border-[var(--studio-divider)] bg-[var(--studio-sidebar)] px-4 md:hidden">
        <button
          type="button"
          aria-label="Open menu"
          data-testid="ops-menu"
          onClick={() => setDrawerOpen(true)}
          className="flex h-10 w-10 items-center justify-center rounded-[12px] text-[var(--text-secondary)] hover:bg-[var(--studio-raised)]"
        >
          <Icon name="menu" size={22} />
        </button>
        <span className="truncate font-sans text-base font-bold text-[var(--text-primary)]">Hans Ops</span>
        <span className="w-10" />
      </header>

      {/* Mobile drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="studio w-[256px] border-[var(--studio-divider)] bg-[var(--studio-sidebar)] p-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarInner onNavigate={() => setDrawerOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Content */}
      <main id="ops-main" className="min-h-screen pt-14 md:ml-64 md:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
